import { Loader } from 'common/components/Loader/Loader';
import { AI_ACTIONS_BTN } from 'common/constants/AISimulationConstants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import AIShare from '../AIShare/AIShare';

import {
	useState,
	Box,
	Icon,
	ListItem,
	Paper,
	ListItemIcon,
	Stack,
	Tooltip,
	Typography,
	useDispatch,
	useSelector,
	isEmpty,
	TableComponent,
	dateInCorrectFormatWithFullYear,
	EditIcon,
	DeleteIcon,
	DownloadIcon,
	ShareIcon,
	AI_SIMULATION_TYPES,
	AI_TYPE,
	IAITableprops,
	ControlTestStepActions,
	generateResultPDF,
	CONTROL_TESTSCRIPT_ACTIONS_TYPE,
	useTableQuery,
	setSimulationCommonShareDrawer,
	aiModelPDFDownload,
	RootState,
	Column,
	IAIRecipeItem,
	IObligationToPolicyOutput,
	styles,
} from './AITableModules';

const { iconFillColor } = styles;

const AITable = (props: IAITableprops) => {
	const { RECIPE } = AI_TYPE;
	const { EDIT, DELETE, DOWNLOAD, SHARE } = AI_ACTIONS_BTN;
	const { DELETE_TEST_ACTION } = CONTROL_TESTSCRIPT_ACTIONS_TYPE;

	const {
		data,
		isLoading,
		type,
		totalItems,
		onClickDeleteHandler,
		onClickEditHandler,
		handlePagination,
	} = props;
	const { IS_EXPORT_BUTTON_ENABLED } = FeatureFlagsKeysEnum;

	const simulationTabIndex = useSelector(
		(state: RootState) => state?.simulationArena?.simulationTabIndex,
	);

	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);

	const isExportButtonEnabled = useFeatureFlag(
		IS_EXPORT_BUTTON_ENABLED,
		isLDResponseExists,
	);

	const [typeBtn, setTypeBtn] = useState<string>('');
	const [selectedIndex, setSelectedIndex] = useState<string>('');
	const [hoverIcon, setHoverIcon] = useState<boolean>(false);
	const [deleteModal, setDeleteModal] = useState<boolean>(false);
	const [rowData, setRowData] = useState<any>();

	const dispatch = useDispatch();

	const { tableQueryObj, updateTableQuery, updateQueryFlag } = useTableQuery({
		id: `simulationtableId_${simulationTabIndex}`,
	});

	const setListHover = (index: string) => {
		setHoverIcon(true);
		setSelectedIndex(index);
	};

	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag) {
			updateTableQuery(data);
			handlePagination(data);
		}
	};

	const onHideListIcons = () => {
		setHoverIcon(false);
	};

	const onClickEdit = (paramData: any) => {
		updateQueryFlag(true);
		onClickEditHandler(paramData, type);
		setTypeBtn(EDIT);
	};

	const getType = (params: any) => {
		if (type === RECIPE && params?.models?.length) {
			return params.models[params.models.length - 1].workflowStep;
		} else if (params?.workflowStep) {
			return params.workflowStep;
		}
		return null; // Handle the case when neither condition is true
	};

	const handleAction = (rowData: any) => {
		setDeleteModal(true);
		setRowData(rowData);
		setTypeBtn(DELETE);
	};

	const onClickDelete = (rowData: any) => {
		onClickDeleteHandler(rowData, type);
		setDeleteModal(false);
	};

	const onClickShare = (rowData: any) => {
		dispatch(setSimulationCommonShareDrawer(true));
		setRowData(rowData);
		setTypeBtn(SHARE);
	};

	const handleActionsModalClose = () => {
		setDeleteModal(false);
		setRowData(undefined);
	};

	const selectedAIRecipe = useSelector(
		(state: RootState) => state.simulationArena.selectedAIRecipe,
	);

	const { CONTROL_QUALITY_GRAMMAR_CHECK, OBLIGATION_TO_POLICY } =
		AI_SIMULATION_TYPES;

	// eslint-disable-next-line consistent-return
	const downloadHandler = (rowData: any) => {
		setTypeBtn(DOWNLOAD);
		const fileName = rowData?.name;
		const AiRecipePageTitle = 'Regulation obligations and control generation';
		const modelTitle = 'Insert Plain English Summary';
		const models: IAIRecipeItem[] = rowData?.models;
		const lastModel = models ? models[models.length - 1] : undefined;

		const input = type === AI_TYPE.RECIPE ? lastModel?.input : rowData?.input;
		const modelType =
			type === RECIPE ? lastModel?.workflowStep : rowData?.workflowStep;
		const output = type === RECIPE ? lastModel?.output : rowData?.output;
		const handleObligationToPolicyDownload = (models: any) =>
			models.some((model: any) => model.workflowStep === OBLIGATION_TO_POLICY);
		const isObliToPolicy = models
			? handleObligationToPolicyDownload(models)
			: false;

		if (isObliToPolicy) {
			const obligationToPolicyOutput =
				lastModel?.output as IObligationToPolicyOutput[];

			generateResultPDF(obligationToPolicyOutput);
		} else if (rowData?.workflowStep === CONTROL_QUALITY_GRAMMAR_CHECK) {
			try {
				// Attempt to parse the JSON string
				const parsedObject = JSON.parse(rowData.output);
				aiModelPDFDownload(
					fileName,
					CONTROL_QUALITY_GRAMMAR_CHECK,
					CONTROL_QUALITY_GRAMMAR_CHECK,
					{
						inputTitle: 'Language Quality Check Content',
						inputValue: parsedObject.control_content,
					},
					{
						outputTitle: 'Updated Control Description',
						outputValue: parsedObject.updatedDescription,
					},
					undefined,
					{
						fiveWResponse: JSON.stringify(parsedObject?.five_ws),
						grammarScore: JSON.stringify(parsedObject?.grammarScore),
						controlMapping: JSON.stringify(parsedObject?.controlMapping),
						additionalInfo: parsedObject?.additional_info,
					},
				);
			} catch (error) {
				// Handle the error gracefully
				// eslint-disable-next-line no-console
				console.error('Failed to parse JSON string:', error);
				return null; // Return null or a default object
			}
		} else {
			aiModelPDFDownload(
				isEmpty(selectedAIRecipe) ? null : selectedAIRecipe.name,
				fileName,
				AiRecipePageTitle,
				{
					inputTitle: modelTitle,
					inputValue: input,
				},
				{
					outputTitle: modelType,
					outputValue: output,
				},
			);
		}
	};

	const generateSharedData = () => {
		if (type === RECIPE) {
			return {
				id: rowData?.id,
				name: rowData?.name,
				models: rowData?.models,
				type: rowData?.type,
			};
		}
		return {
			id: rowData?.id,
			name: rowData?.name,
			type: rowData?.type,
			input: rowData?.input,
			output: rowData?.output,
			workflowStep: rowData?.workflowStep,
		};
	};

	const columns: readonly Column[] = [
		{
			id: 'name',
			label: 'Simulation Name',
			disablePadding: false,
			render: (params: any) => (
				<Typography
					component="div"
					data-testid="name-link"
					className={styles['ai-table-task-name']}
					onClick={() => onClickEdit(params)}
				>
					{params?.name}
				</Typography>
			),
		},
		{
			id: 'type',
			label: 'Workflow Step',
			disablePadding: false,
			render: (params: any) => (
				<Typography component="div" data-testid="taskname-link">
					{getType(params)}
				</Typography>
			),
		},
		{
			id: 'lastUpdated',
			label: 'Created Date',
			disablePadding: false,
			render: (params: any) => (
				<Typography>
					<span>{dateInCorrectFormatWithFullYear(params.lastUpdated)}</span>
				</Typography>
			),
		},
		{
			id: 'actions',
			label: 'Action',
			disablePadding: false,
			render: (row: any) => (
				<ListItem
					className={styles['ai-icon-actions']}
					data-testid="list-actions"
					onMouseOver={() => {
						setListHover(row.id);
					}}
					onFocus={() => {
						setListHover(row.id);
					}}
					onMouseOut={() => {
						onHideListIcons();
					}}
					onBlur={() => {
						onHideListIcons();
					}}
				>
					{hoverIcon && selectedIndex === row.id ? (
						<ListItemIcon
							sx={{
								'& :hover': {
									cursor: 'not-allowed',
								},
								paddingLeft: '0px',
							}}
						>
							<Stack direction="row" className={styles['ai-list-item']}>
								<Tooltip
									title="Edit"
									placement="top"
									data-testid="edit-tooltip"
								>
									<Icon>
										<EditIcon
											data-testid="edit-icon"
											onClick={() => onClickEdit(row)}
											fill={iconFillColor}
										/>
									</Icon>
								</Tooltip>
								<Tooltip
									title="Delete"
									placement="top"
									data-testid="delete-tooltip"
								>
									<Icon>
										<DeleteIcon
											data-testid="delete-icon"
											onClick={() => handleAction(row)}
											fill={iconFillColor}
										/>
									</Icon>
								</Tooltip>
								<Tooltip
									title="Download"
									placement="top"
									data-testid="download-tooltip"
								>
									<Icon
										className={
											isExportButtonEnabled ? styles['btn-disabled'] : ''
										}
									>
										<DownloadIcon
											data-testid="download-icon"
											fill={iconFillColor}
											onClick={() => downloadHandler(row)}
										/>
									</Icon>
								</Tooltip>
								<Tooltip
									title="Share"
									placement="top"
									data-testid="Share-tooltip"
								>
									<Icon>
										<ShareIcon
											className={styles['share-icon-style']}
											fill={iconFillColor}
											data-testid="share-icon"
											onClick={() => onClickShare(row)}
										/>
									</Icon>
								</Tooltip>
							</Stack>
						</ListItemIcon>
					) : null}
				</ListItem>
			),
		},
	];

	return (
		<>
			<Box className={styles['ai-list-container']} data-testId="ai-box-section">
				{data && data.length > 0 && !isLoading ? (
					<TableComponent
						defaultQuery={tableQueryObj}
						rows={data}
						columns={columns}
						defaultSortKey=""
						tableStyles={{ minWidth: 750, maxHeight: 440 }}
						totalItems={totalItems}
						onPageHandler={handlePageChange}
						isRenderPagination
					/>
				) : (
					<div>
						{!isLoading ? (
							<Paper className={styles['aitable-table-loading-box']}>
								<Typography data-testid="no-data-found">
									No data found
								</Typography>
							</Paper>
						) : (
							<Paper>
								<Loader show={isLoading} />
							</Paper>
						)}
					</div>
				)}
				{deleteModal && (
					<ControlTestStepActions
						rowIndex={type}
						onClickDelete={onClickDelete}
						openModal={deleteModal}
						title="Confirm Delete"
						handleClose={handleActionsModalClose}
						handleAction={handleAction}
						testScriptData={rowData}
						ModalHeaderImg={null}
						type={DELETE_TEST_ACTION}
						alertText="This action cannot be undone."
					/>
				)}
			</Box>
			<AIShare
				typeBtn={typeBtn}
				aiSharedData={generateSharedData()}
				type={type}
			/>
		</>
	);
};
export default AITable;
